import { type z } from 'zod';

import { assertArrayIsNotEmpty } from '@craft/lib';

export enum EnvironmentVariableErrorCode {
  Invalid = 'Invalid',
  Missing = 'Missing',
  Unknown = 'Unknown',
}

const stringifyDetail = (detail: string | z.ZodIssue) =>
  typeof detail === 'string' ? detail : `(code = ${detail.code}) ${detail.message}`;

const isEnvironmentVariableErrorCode = (value: unknown): value is EnvironmentVariableErrorCode =>
  Object.values(EnvironmentVariableErrorCode).includes(value as EnvironmentVariableErrorCode);

const isZodError = (
  error?: EnvironmentVariableErrorCode | string | z.ZodError,
): error is z.ZodError => typeof error !== 'string' && error?.errors !== undefined;

const DefaultEnvironmentVariableErrorMessages: Record<
  EnvironmentVariableErrorCode,
  (source: string, punctuation?: '!' | '.' | ':') => string
> = {
  [EnvironmentVariableErrorCode.Invalid]: (source, punctuation = '!') =>
    `The environment variable '${source}' is invalid${punctuation}`,
  [EnvironmentVariableErrorCode.Missing]: (source, punctuation = '!') =>
    `The environment variable '${source}' is required but not in the environment${punctuation}`,
  [EnvironmentVariableErrorCode.Unknown]: (source, punctuation = '!') =>
    `The environment variable '${source}' is invalid or missing${punctuation}`,
};

const parseEnvironmentVariableErrorMessage = (
  source: string,
  error?: EnvironmentVariableErrorCode | string | z.ZodError,
  code?: EnvironmentVariableErrorCode,
): string => {
  const parsedCode = isEnvironmentVariableErrorCode(error) ? error : code;
  const parsedError = isEnvironmentVariableErrorCode(error) ? undefined : error;

  if (isZodError(parsedError)) {
    assertArrayIsNotEmpty(parsedError.errors);
    const prefix = DefaultEnvironmentVariableErrorMessages[EnvironmentVariableErrorCode.Invalid](
      source,
      ':',
    );
    if (parsedError.errors.length > 1) {
      return [
        prefix,
        ...parsedError.errors.map((detail, index) => `${index + 1}. ${stringifyDetail(detail)}`),
      ].join(' ');
    }
    return `${prefix} ${stringifyDetail(parsedError.errors[0])}`;
  }

  if (parsedError && parsedCode) {
    const prefix = DefaultEnvironmentVariableErrorMessages[parsedCode](source, ':');
    return [prefix, error].join(' ');
  }
  return (
    parsedError ??
    DefaultEnvironmentVariableErrorMessages[parsedCode ?? EnvironmentVariableErrorCode.Unknown](
      source,
    )
  );
};

export abstract class AbstractEnvironmentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvironmentError';
  }
}

abstract class AbstractEnvironmentVariableError extends AbstractEnvironmentError {
  readonly source: string;

  constructor(source: string, message: string) {
    super(message);
    this.message = message;
    this.source = source;
  }

  public abstract get issues(): (string | z.ZodIssue)[];
}

/**
 * An {@link Error} that is thrown when an environment variable is invalid or missing.
 */
export class EnvironmentVariableError extends AbstractEnvironmentVariableError {
  public readonly code: EnvironmentVariableErrorCode;
  public readonly error: undefined | z.ZodError;

  constructor(source: string, error?: z.ZodError);
  constructor(source: string, code?: EnvironmentVariableErrorCode);
  constructor(source: string, message?: string, code?: EnvironmentVariableErrorCode);
  constructor(
    source: string,
    error?: EnvironmentVariableErrorCode | string | z.ZodError,
    code?: EnvironmentVariableErrorCode,
  ) {
    super(source, parseEnvironmentVariableErrorMessage(source, error, code));
    this.error = isZodError(error) ? error : undefined;
    this.code = isEnvironmentVariableErrorCode(error)
      ? error
      : isZodError(error)
        ? EnvironmentVariableErrorCode.Invalid
        : (code ?? EnvironmentVariableErrorCode.Unknown);
  }

  public static Invalid(source: string, message?: string) {
    return new EnvironmentVariableError(source, message, EnvironmentVariableErrorCode.Invalid);
  }

  public static Missing(source: string, message?: string) {
    return new EnvironmentVariableError(source, message, EnvironmentVariableErrorCode.Missing);
  }

  public get issues(): (string | z.ZodIssue)[] {
    if (this.error && isZodError(this.error)) {
      return this.error.errors;
    }
    return [this.message];
  }
}
